import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Table, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { Plus, Edit, Trash2, ArrowUp, ArrowDown, Sparkles, Loader2, CheckCircle2, ArrowRight, ChevronLeft, ChevronRight, Wand2, Check } from 'lucide-react'
import { useEngineQuestions, useCreateEngineQuestion, useUpdateEngineQuestion, useDeleteEngineQuestion } from '../hooks'
import { useRevaluateWeights, useGenerateQuestions } from '@/features/ai/hooks'
import type { WeightChange } from '@/features/ai/hooks'
import { engineQuestionsApi } from '../api'
import { useEngineDomains } from '@/features/domains/hooks'
import { useEngineMaturityLevels } from '@/features/maturity-levels/hooks'
import { useEngineScoreLabels } from '@/features/scoring-labels/hooks'
import type { QuestionType } from '../types'
import { ImportExportButtons } from '@/components/ui/import-export-buttons'
import { useListState } from '@/hooks/use-list-state'
import { DataToolbar } from '@/components/ui/data-toolbar'
import { DataPagination } from '@/components/ui/data-pagination'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'

interface Question {
  id: string
  domain_id: string
  domain_name?: string
  category: string
  text: string
  description: string
  question_type: QuestionType
  weight: number
  maturity_level?: number
  answer_options: Array<{
    value: number
    label: string
    description: string
  }>
  sort_order: number
  is_active: boolean
  justification_for_yes?: string
  justification_for_no?: string
  recommendation?: string
  created_at: string
  updated_at: string
}

interface Domain {
  id: string
  name: string
}

const YES_NO_OPTIONS = [
  { value: 0, label: 'No', description: '' },
  { value: 1, label: 'Yes', description: '' },
]

const DEFAULT_MCQ_OPTIONS = [
  { value: 0, label: 'Not Implemented', description: 'No evidence of this capability' },
  { value: 1, label: 'Partially Implemented', description: 'Some evidence, but inconsistent' },
  { value: 2, label: 'Mostly Implemented', description: 'Generally implemented with minor gaps' },
  { value: 3, label: 'Fully Implemented', description: 'Completely implemented and effective' },
  { value: 4, label: 'Excellence', description: 'Exceeds expectations, best practices' },
]

interface FormData {
  id: string
  domain_id: string
  category: string
  text: string
  description: string
  question_type: QuestionType
  weight: number
  maturity_level: number
  sort_order: number
  justification_for_yes: string
  justification_for_no: string
  recommendation: string
  answer_options: Array<{ value: number; label: string; description: string }>
}

const getDefaultFormData = (): FormData => ({
  id: '',
  domain_id: '',
  category: '',
  text: '',
  description: '',
  question_type: 'mcq',
  weight: 1.0,
  maturity_level: 1,
  sort_order: 0,
  justification_for_yes: '',
  justification_for_no: '',
  recommendation: '',
  answer_options: [...DEFAULT_MCQ_OPTIONS],
})

const QuestionManagement: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null)
  const [formData, setFormData] = useState<FormData>(getDefaultFormData())
  const [showRevaluateDialog, setShowRevaluateDialog] = useState(false)
  const [changesPage, setChangesPage] = useState(1)
  const [showGenerateDialog, setShowGenerateDialog] = useState(false)
  const [generatePrompt, setGeneratePrompt] = useState('')
  const [generateDomainId, setGenerateDomainId] = useState('')
  const [generateLevel, setGenerateLevel] = useState('')
  const [generateType, setGenerateType] = useState('')
  const [generateCount, setGenerateCount] = useState(5)
  const [selectedGenerated, setSelectedGenerated] = useState<Set<number>>(new Set())

  const listState = useListState({ defaultPerPage: 10, defaultSortBy: 'maturity_level', defaultSortOrder: 'asc' })

  // Fetch domains for dropdown
  const { data: domainsData } = useEngineDomains({ per_page: 100 })
  // Fetch maturity levels for dropdown (single source of truth from DB)
  const { data: maturityLevelsRaw } = useEngineMaturityLevels()
  const maturityLevels = (maturityLevelsRaw as any)?.data ?? maturityLevelsRaw ?? []
  // Fetch score labels for MCQ integration
  const { data: scoreLabelsRaw } = useEngineScoreLabels()
  const scoreLabels = (scoreLabelsRaw as any)?.data ?? scoreLabelsRaw ?? []

  // Build query params with filters for the questions API
  const questionsParams = {
    ...listState.queryParams,
    ...(listState.filters.domain_id ? { domain_id: Array.isArray(listState.filters.domain_id) ? listState.filters.domain_id.join(',') : listState.filters.domain_id } : {}),
    ...(listState.filters.maturity_level ? { maturity_level: Array.isArray(listState.filters.maturity_level) ? listState.filters.maturity_level.join(',') : listState.filters.maturity_level } : {}),
    ...(listState.filters.question_type ? { question_type: Array.isArray(listState.filters.question_type) ? listState.filters.question_type.join(',') : listState.filters.question_type } : {}),
  }

  // Fetch questions
  const { data: questionsData, isLoading, error } = useEngineQuestions(questionsParams)

  // Build maturity level color lookup
  const mlColorMap: Record<number, string> = {}
    ; (Array.isArray(maturityLevels) ? maturityLevels : []).forEach((ml: any) => {
      mlColorMap[ml.level] = ml.color || '#6366f1'
    })

  // Mutations via hooks
  const createQuestionMutation = useCreateEngineQuestion()
  const updateQuestionMutation = useUpdateEngineQuestion()
  const deleteQuestionMutation = useDeleteEngineQuestion()
  const revaluate = useRevaluateWeights()
  const generateQuestionsMutation = useGenerateQuestions()

  const handleStartRevaluate = () => {
    setShowRevaluateDialog(true)
    setChangesPage(1)
    revaluate.run()
  }

  const handleCloseRevaluate = () => {
    setShowRevaluateDialog(false)
    revaluate.reset()
  }

  const resetForm = () => setFormData(getDefaultFormData())

  const handleQuestionTypeChange = (type: QuestionType) => {
    if (type === 'yes_no') {
      setFormData({ ...formData, question_type: type, answer_options: [...YES_NO_OPTIONS] })
    } else {
      setFormData({ ...formData, question_type: type, answer_options: [...DEFAULT_MCQ_OPTIONS] })
    }
  }

  const handleCreate = () => {
    createQuestionMutation.mutate(formData, {
      onSuccess: () => {
        setIsCreateDialogOpen(false)
        resetForm()
      }
    })
  }

  const handleEdit = async (question: Question) => {
    try {
      const res = await engineQuestionsApi.getById(question.id)
      const detailed = res.data?.data || res.data || {}
      let options = detailed.answer_options || DEFAULT_MCQ_OPTIONS
      if (typeof options === 'string') {
        try { options = JSON.parse(options) } catch { options = DEFAULT_MCQ_OPTIONS }
      }
      setSelectedQuestion(question)
      setFormData({
        id: detailed.id || question.id,
        domain_id: detailed.domain_id || question.domain_id,
        category: detailed.category || question.category || '',
        text: detailed.text || question.text,
        description: detailed.description || '',
        question_type: detailed.question_type || 'mcq',
        weight: detailed.weight ?? 1.0,
        maturity_level: detailed.maturity_level ?? 1,
        sort_order: detailed.sort_order ?? question.sort_order,
        justification_for_yes: detailed.justification_for_yes || question.justification_for_yes || '',
        justification_for_no: detailed.justification_for_no || question.justification_for_no || '',
        recommendation: detailed.recommendation || question.recommendation || '',
        answer_options: Array.isArray(options) ? options : DEFAULT_MCQ_OPTIONS,
      })
      setIsEditDialogOpen(true)
    } catch (error) {
      console.error('Failed to load question details:', error)
      setSelectedQuestion(question)
      setFormData({
        id: question.id,
        domain_id: question.domain_id,
        category: question.category || '',
        text: question.text,
        description: '',
        question_type: question.question_type || 'mcq',
        weight: question.weight ?? 1.0,
        maturity_level: (question as any).maturity_level ?? 1,
        sort_order: question.sort_order,
        justification_for_yes: (question as any).justification_for_yes || '',
        justification_for_no: (question as any).justification_for_no || '',
        recommendation: (question as any).recommendation || '',
        answer_options: DEFAULT_MCQ_OPTIONS,
      })
      setIsEditDialogOpen(true)
    }
  }

  const handleUpdate = () => {
    if (selectedQuestion) {
      updateQuestionMutation.mutate({ id: selectedQuestion.id, data: formData }, {
        onSuccess: () => {
          setIsEditDialogOpen(false)
        }
      })
    }
  }

  const handleDelete = (id: string) => {
    deleteQuestionMutation.mutate(id)
  }

  const updateAnswerOption = (index: number, field: string, value: string) => {
    const newOptions = [...(formData.answer_options || [])]
    newOptions[index] = { ...newOptions[index], [field]: value }
    setFormData({ ...formData, answer_options: newOptions })
  }

  const addAnswerOption = () => {
    const opts = formData.answer_options || []
    const newValue = opts.length
    setFormData({
      ...formData,
      answer_options: [...opts, { value: newValue, label: '', description: '' }]
    })
  }

  const removeAnswerOption = (index: number) => {
    if ((formData.answer_options || []).length > 2) {
      const newOptions = (formData.answer_options || [])
        .filter((_, i) => i !== index)
        .map((opt, i) => ({ ...opt, value: i })) // Re-index values from 0
      setFormData({ ...formData, answer_options: newOptions })
    }
  }

  const moveOption = (index: number, direction: 'up' | 'down') => {
    const opts = [...(formData.answer_options || [])]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= opts.length) return
      ;[opts[index], opts[targetIndex]] = [opts[targetIndex], opts[index]]
    // Re-index values from 0 after reorder
    const reindexed = opts.map((opt, i) => ({ ...opt, value: i }))
    setFormData({ ...formData, answer_options: reindexed })
  }

  const useDefaultScoreLabels = () => {
    setFormData({ ...formData, answer_options: [...DEFAULT_MCQ_OPTIONS] })
  }

  /** Populate MCQ answer options from score labels in the database */
  const useScoreLabelsFromDB = () => {
    if (scoreLabels.length > 0) {
      const opts = scoreLabels
        .sort((a: any, b: any) => (a.value ?? 0) - (b.value ?? 0))
        .map((sl: any) => ({
          value: sl.value ?? 0,
          label: sl.label ?? '',
          description: sl.description ?? '',
        }))
      setFormData({ ...formData, answer_options: opts })
    }
  }

  if (!questionsData && isLoading) return <div>Loading...</div>
  if (error) return <div>Error loading questions</div>

  const questions = questionsData?.items || []
  const pagination = questionsData ? { total: questionsData.total, page: questionsData.page, per_page: questionsData.per_page, pages: questionsData.pages } : undefined
  const domains = domainsData?.data || []

  const isYesNo = formData.question_type === 'yes_no'

  // ── Shared form fields component ────────────────────────────────────
  const renderQuestionFormFields = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="question-type">Question Type</Label>
          <Select value={formData.question_type} onValueChange={(v) => handleQuestionTypeChange(v as QuestionType)}>
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mcq">Multiple Choice</SelectItem>
              <SelectItem value="yes_no">Yes / No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="maturity-level">Maturity Level</Label>
          <Select value={String(formData.maturity_level)} onValueChange={(v) => setFormData({ ...formData, maturity_level: parseInt(v) || 1 })}>
            <SelectTrigger id="maturity-level">
              <SelectValue placeholder="Select level" />
            </SelectTrigger>
            <SelectContent>
              {maturityLevels.length > 0 ? (
                maturityLevels.map((ml: any) => (
                  <SelectItem key={ml.level} value={String(ml.level)}>
                    Level {ml.level}: {ml.name}
                  </SelectItem>
                ))
              ) : (
                [1, 2, 3, 4, 5].map(l => (
                  <SelectItem key={l} value={String(l)}>Level {l}</SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Which Gartner level does this question assess?
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="weight">Weight</Label>
          <Input
            id="weight"
            type="number"
            step="0.1"
            min="0"
            value={formData.weight}
            onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) || 0 })}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Higher weight = more impact on domain score
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="domain">Domain</Label>
          <Select value={formData.domain_id} onValueChange={(value) => setFormData({ ...formData, domain_id: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Select domain" />
            </SelectTrigger>
            <SelectContent>
              {domains.map((domain: Domain) => (
                <SelectItem key={domain.id} value={domain.id}>
                  {domain.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="category">Category</Label>
          <Input
            id="category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            placeholder="e.g., System Uptime"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="text">Question Text</Label>
        <Textarea
          id="text"
          value={formData.text}
          onChange={(e) => setFormData({ ...formData, text: e.target.value })}
          placeholder="What is your current system uptime percentage?"
        />
      </div>
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Additional context for the question..."
        />
      </div>
      <div>
        <Label htmlFor="justification_for_yes">Justification for Yes (Why Yes)</Label>
        <Textarea
          id="justification_for_yes"
          value={formData.justification_for_yes}
          onChange={(e) => setFormData({ ...formData, justification_for_yes: e.target.value })}
          placeholder="What evidence indicates this requirement is met?"
        />
      </div>
      <div>
        <Label htmlFor="justification_for_no">Justification for No (Why No)</Label>
        <Textarea
          id="justification_for_no"
          value={formData.justification_for_no}
          onChange={(e) => setFormData({ ...formData, justification_for_no: e.target.value })}
          placeholder="What indicates a failure or gap for this requirement?"
        />
      </div>
      <div>
        <Label htmlFor="recommendation">Recommendation</Label>
        <Textarea
          id="recommendation"
          value={formData.recommendation}
          onChange={(e) => setFormData({ ...formData, recommendation: e.target.value })}
          placeholder="What should be done to meet this requirement?"
        />
      </div>
      <div>
        <Label htmlFor="sort_order">Sort Order</Label>
        <Input
          id="sort_order"
          type="number"
          value={formData.sort_order}
          onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
        />
      </div>

      {/* Answer Options */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Answer Options</Label>
          {!isYesNo && (
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={useScoreLabelsFromDB} className="flex items-center gap-1">
                Use Score Labels
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={useDefaultScoreLabels} className="flex items-center gap-1">
                Use Defaults
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={addAnswerOption} className="flex items-center gap-1">
                <Plus className="w-3 h-3" />
                Add Option
              </Button>
            </div>
          )}
        </div>

        {isYesNo ? (
          <div className="p-4 border rounded bg-muted/30">
            <p className="text-sm text-muted-foreground mb-3">Yes/No questions have fixed answer options:</p>
            <div className="space-y-2">
              {YES_NO_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-center gap-3 p-2 border rounded bg-background">
                  <Badge variant="outline" className="font-mono">{opt.value}</Badge>
                  <span className="font-medium">{opt.label}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {(formData.answer_options || []).map((option, index) => (
              <div key={index} className="grid grid-cols-[auto_60px_1fr_1fr_auto] gap-2 p-3 border rounded items-end">
                <div className="flex flex-col gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    disabled={index === 0}
                    onClick={() => moveOption(index, 'up')}
                  >
                    <ArrowUp className="w-3 h-3" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    disabled={index === (formData.answer_options || []).length - 1}
                    onClick={() => moveOption(index, 'down')}
                  >
                    <ArrowDown className="w-3 h-3" />
                  </Button>
                </div>
                <div>
                  <Label className="text-xs">Value</Label>
                  <Input
                    value={option.value}
                    readOnly
                    disabled
                    className="font-mono bg-muted text-center"
                  />
                </div>
                <div>
                  <Label className="text-xs">Label</Label>
                  <Input
                    value={option.label}
                    onChange={(e) => updateAnswerOption(index, 'label', e.target.value)}
                    placeholder="e.g., Not Implemented"
                  />
                </div>
                <div>
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={option.description}
                    onChange={(e) => updateAnswerOption(index, 'description', e.target.value)}
                    placeholder="e.g., No evidence"
                  />
                </div>
                <div className="flex items-end">
                  {(formData.answer_options || []).length > 2 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeAnswerOption(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-2">
          {isYesNo
            ? 'Yes/No answers are fixed. No = 0, Yes = 1.'
            : 'Values are auto-assigned from 0. Use the arrows to reorder options.'}
        </p>
      </div>
    </div>
  )

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Question Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage assessment questions and their answer options
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AuthorizationGate actions={['ai_revaluate_weights']}>
            <Button variant="outline" onClick={handleStartRevaluate} disabled={revaluate.isRunning}>
              {revaluate.isRunning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {revaluate.isRunning ? 'Revaluating...' : 'AI Revaluate Weights'}
            </Button>
          </AuthorizationGate>
          <AuthorizationGate actions={['ai_generate_questions']}>
            <Button variant="outline" onClick={() => setShowGenerateDialog(true)}>
              <Wand2 className="w-4 h-4 mr-2" />
              AI Generate
            </Button>
          </AuthorizationGate>
          <AuthorizationGate actions={['export_questions', 'import_questions']} mode="any">
            <ImportExportButtons
              entityType="questions"
              entityLabel="Questions"
            />
          </AuthorizationGate>
          <AuthorizationGate actions={['create_question']}>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Question
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Question</DialogTitle>
                  <DialogDescription>
                    Add a new assessment question to the system
                  </DialogDescription>
                </DialogHeader>
                {/* Question ID field (create only) */}
                <div>
                  <Label htmlFor="id">Question ID</Label>
                  <Input
                    id="id"
                    value={formData.id}
                    onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                    placeholder="e.g., avail_1"
                  />
                </div>
                {renderQuestionFormFields()}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreate} disabled={createQuestionMutation.isPending}>
                    {createQuestionMutation.isPending ? 'Creating...' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </AuthorizationGate>
        </div>
      </div>

      {/* Toolbar — replaces the hand-coded search + filter dropdowns */}
      <DataToolbar
        listState={listState}
        searchPlaceholder="Search questions..."
        sortOptions={[
          { label: 'Maturity Level', value: 'maturity_level' },
          { label: 'Weight', value: 'weight' },
          { label: 'Domain', value: 'domain_name' },
          { label: 'Created', value: 'created_at' },
        ]}
        filters={[
          {
            key: 'domain_id',
            label: 'Domain',
            options: domains.filter((d: Domain) => d.id).map((d: Domain) => ({ label: d.name, value: d.id })),
          },
          {
            key: 'maturity_level',
            label: 'Level',
            options: (Array.isArray(maturityLevels) ? maturityLevels : []).map((ml: any) => ({
              label: `L${ml.level}: ${ml.name}`,
              value: String(ml.level),
              color: ml.color || '#6366f1',
            })),
          },
          {
            key: 'question_type',
            label: 'Type',
            options: [
              { label: 'Yes/No', value: 'yes_no' },
              { label: 'MCQ', value: 'mcq' },
            ],
          },
        ]}
        totalItems={pagination?.total}
      />

      {/* Questions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1 }}
      >
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Domain</TableHead>
                  <TableHead className="w-[160px]">Category</TableHead>
                  <TableHead>Question</TableHead>
                  <TableHead className="w-[80px]">Level</TableHead>
                  <TableHead className="w-[70px]">Type</TableHead>
                  <TableHead className="w-[70px]">Weight</TableHead>
                  <TableHead className="w-[90px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <tbody>
                {questions.map((question: Question, idx: number) => (
                  <tr
                    key={`${listState.page}-${question.id}`}
                    className="border-b transition-colors hover:bg-muted/50"
                    style={{ animation: `fadeInRow 0.2s ease-out ${idx * 0.02}s both` }}
                  >
                    <TableCell className="py-1">
                      <Badge variant="outline" className="text-[11px] px-1.5 py-0">{question.domain_name}</Badge>
                    </TableCell>
                    <TableCell className="py-1 text-xs text-muted-foreground">{question.category || '—'}</TableCell>
                    <TableCell className="py-1 text-sm">
                      <span className="line-clamp-2">{question.text}</span>
                    </TableCell>
                    <TableCell className="py-1">
                      {question.maturity_level ? (
                        <Badge
                          variant="outline"
                          className="text-[11px] px-1.5 py-0"
                          style={{
                            borderColor: mlColorMap[question.maturity_level] || '#6366f1',
                            color: mlColorMap[question.maturity_level] || '#6366f1',
                          }}
                        >
                          Level {question.maturity_level}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-1">
                      <Badge variant={question.question_type === 'yes_no' ? 'secondary' : 'default'} className="text-[11px] px-1.5 py-0">
                        {question.question_type === 'yes_no' ? 'Yes/No' : 'MCQ'}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-1">
                      <span className="font-mono text-xs">{question.weight ?? 1.0}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <AuthorizationGate actions={['update_question']}>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(question)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </AuthorizationGate>
                        <AuthorizationGate actions={['delete_question']}>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Question</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this question? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(question.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </AuthorizationGate>
                      </div>
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination */}
            {pagination && (
              <DataPagination
                page={listState.page}
                totalPages={pagination.pages}
                onPageChange={listState.setPage}
                totalItems={pagination.total}
                perPage={listState.perPage}
              />
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Question</DialogTitle>
            <DialogDescription>
              Update the question information
            </DialogDescription>
          </DialogHeader>
          {renderQuestionFormFields()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateQuestionMutation.isPending}>
              {updateQuestionMutation.isPending ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Revaluation Progress Dialog */}
      <Dialog open={showRevaluateDialog} onOpenChange={(open) => { if (!open && !revaluate.isRunning) handleCloseRevaluate() }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" onInteractOutside={(e) => { if (revaluate.isRunning) e.preventDefault() }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {revaluate.done ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : (
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              )}
              {revaluate.done ? 'Weight Revaluation Complete' : 'Revaluating Weights...'}
            </DialogTitle>
            <DialogDescription>
              {revaluate.done
                ? `AI updated ${revaluate.changes.length} question weight${revaluate.changes.length !== 1 ? 's' : ''}.`
                : revaluate.progress
                  ? `Processing batch ${revaluate.progress.batch} of ${revaluate.progress.total_batches}...`
                  : 'Starting AI analysis...'
              }
            </DialogDescription>
          </DialogHeader>

          {/* Progress bar */}
          {revaluate.progress && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {revaluate.progress.domain} · Level {revaluate.progress.level}
                  {revaluate.progress.count ? ` (${revaluate.progress.count} questions)` : ''}
                </span>
                <span className="font-mono text-xs">
                  {revaluate.progress.batch}/{revaluate.progress.total_batches}
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${(revaluate.progress.batch / revaluate.progress.total_batches) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Errors */}
          {revaluate.errors.length > 0 && (
            <div className="space-y-1">
              {revaluate.errors.map((err, i) => (
                <div key={i} className="text-sm text-red-600 bg-red-50 dark:bg-red-950/20 p-2 rounded">
                  {err.domain && <span className="font-medium">{err.domain} L{err.level}: </span>}
                  {err.message}
                </div>
              ))}
            </div>
          )}

          {/* Weight changes table */}
          {revaluate.changes.length > 0 && (() => {
            const perPage = 5
            const totalPages = Math.ceil(revaluate.changes.length / perPage)
            const pageItems = revaluate.changes.slice((changesPage - 1) * perPage, changesPage * perPage)
            return (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    <Badge variant="secondary" className="mr-2">{revaluate.changes.length}</Badge>
                    weight{revaluate.changes.length !== 1 ? 's' : ''} changed
                  </span>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={changesPage <= 1} onClick={() => setChangesPage(p => p - 1)}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-xs font-mono px-2">{changesPage}/{totalPages}</span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={changesPage >= totalPages} onClick={() => setChangesPage(p => p + 1)}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Question</TableHead>
                        <TableHead className="w-[180px] text-center">Weight Change</TableHead>
                      </TableRow>
                    </TableHeader>
                    <tbody>
                      {pageItems.map((change: WeightChange) => {
                        const diff = change.new_weight - change.old_weight
                        const isIncrease = diff > 0
                        return (
                          <TableRow key={change.id}>
                            <TableCell className="text-sm">
                              <span className="line-clamp-2">{change.text}</span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-2 text-sm">
                                <span className="font-mono text-muted-foreground">{change.old_weight.toFixed(1)}</span>
                                <ArrowRight className="w-3 h-3 text-muted-foreground" />
                                <span className={`font-mono font-semibold ${isIncrease ? 'text-green-600' : 'text-red-600'}`}>
                                  {change.new_weight.toFixed(1)}
                                </span>
                                <Badge variant="outline" className={`text-[10px] px-1 ${isIncrease ? 'text-green-600 border-green-300' : 'text-red-600 border-red-300'}`}>
                                  {isIncrease ? '+' : ''}{diff.toFixed(1)}
                                </Badge>
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              </div>
            )
          })()}

          {/* No changes yet message */}
          {revaluate.done && revaluate.changes.length === 0 && revaluate.errors.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No weight changes were made — all weights are already optimal.
            </p>
          )}

          <DialogFooter>
            <Button onClick={handleCloseRevaluate} disabled={revaluate.isRunning}>
              {revaluate.isRunning ? 'Processing...' : 'Done'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate Questions Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={(open) => {
        if (!open && !generateQuestionsMutation.isRunning) {
          setShowGenerateDialog(false)
          generateQuestionsMutation.reset()
          setSelectedGenerated(new Set())
          setGeneratePrompt('')
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-primary" />
              AI Generate Questions
            </DialogTitle>
            <DialogDescription>
              Describe what questions you need and the AI will generate them for preview.
            </DialogDescription>
          </DialogHeader>

          {/* Prompt + options */}
          <div className="space-y-4">
            <div>
              <Label>Prompt</Label>
              <Textarea
                value={generatePrompt}
                onChange={(e) => setGeneratePrompt(e.target.value)}
                placeholder="e.g. Generate questions about cloud security best practices..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Domain (optional)</Label>
                <Select value={generateDomainId} onValueChange={setGenerateDomainId}>
                  <SelectTrigger><SelectValue placeholder="Any domain" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Any domain</SelectItem>
                    {(domainsData?.data || []).map((d: any) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Level (optional)</Label>
                <Select value={generateLevel} onValueChange={setGenerateLevel}>
                  <SelectTrigger><SelectValue placeholder="Any level" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Any level</SelectItem>
                    {(Array.isArray(maturityLevels) ? maturityLevels : []).map((ml: any) => (
                      <SelectItem key={ml.level} value={String(ml.level)}>L{ml.level}: {ml.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={generateType} onValueChange={setGenerateType}>
                  <SelectTrigger><SelectValue placeholder="Any type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Any type</SelectItem>
                    <SelectItem value="yes_no">Yes / No</SelectItem>
                    <SelectItem value="mcq">Multiple Choice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Count</Label>
                <Select value={String(generateCount)} onValueChange={(v) => setGenerateCount(parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[3, 5, 10].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} questions</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              className="w-full"
              disabled={!generatePrompt.trim() || generateQuestionsMutation.isRunning}
              onClick={() => {
                setSelectedGenerated(new Set())
                generateQuestionsMutation.run({
                  prompt: generatePrompt,
                  domain_id: generateDomainId && generateDomainId !== '__all__' ? generateDomainId : undefined,
                  maturity_level: generateLevel && generateLevel !== '__all__' ? parseInt(generateLevel) : undefined,
                  question_type: generateType && generateType !== '__all__' ? generateType : undefined,
                  count: generateCount,
                })
              }}
            >
              {generateQuestionsMutation.isRunning ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {generateQuestionsMutation.status === 'generating' ? 'Generating...' : 'Analyzing...'}</>
              ) : (
                <><Wand2 className="w-4 h-4 mr-2" /> Generate Questions</>
              )}
            </Button>
          </div>

          {/* Generated questions preview */}
          {generateQuestionsMutation.questions.length > 0 && (
            <div className="space-y-3 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  <Badge variant="secondary" className="mr-2">{generateQuestionsMutation.questions.length}</Badge>
                  question{generateQuestionsMutation.questions.length !== 1 ? 's' : ''} {generateQuestionsMutation.done ? 'generated' : 'so far...'} — select which to save
                </span>
                {generateQuestionsMutation.done && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (selectedGenerated.size === generateQuestionsMutation.questions.length) {
                        setSelectedGenerated(new Set())
                      } else {
                        setSelectedGenerated(new Set(generateQuestionsMutation.questions.map((_: any, i: number) => i)))
                      }
                    }}
                  >
                    {selectedGenerated.size === generateQuestionsMutation.questions.length ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {generateQuestionsMutation.questions.map((q: any, i: number) => {
                  const isSelected = selectedGenerated.has(i)
                  return (
                    <div
                      key={i}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${isSelected ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/30'}`}
                      onClick={() => {
                        const next = new Set(selectedGenerated)
                        if (isSelected) next.delete(i)
                        else next.add(i)
                        setSelectedGenerated(next)
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'}`}>
                          {isSelected && <Check className="w-3 h-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{q.text}</p>
                          {q.description && <p className="text-xs text-muted-foreground mt-1">{q.description}</p>}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-[10px]">{q.question_type === 'yes_no' ? 'Yes/No' : 'MCQ'}</Badge>
                            {q.maturity_level && <Badge variant="outline" className="text-[10px]">Level {q.maturity_level}</Badge>}
                            {q.category && <Badge variant="secondary" className="text-[10px]">{q.category}</Badge>}
                            <span className="text-[10px] font-mono text-muted-foreground">w: {q.weight || 1.0}</span>
                          </div>
                          {/* MCQ answer options preview */}
                          {q.question_type === 'mcq' && q.answer_options && q.answer_options.length > 0 && (
                            <div className="mt-2 pl-1 space-y-1">
                              {q.answer_options.map((opt: any, oi: number) => (
                                <div key={oi} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                  <span className="font-mono text-[10px] bg-muted rounded px-1 flex-shrink-0">{opt.value}</span>
                                  <span><span className="font-medium text-foreground">{opt.label}</span>{opt.description ? ` — ${opt.description}` : ''}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Generation error */}
          {generateQuestionsMutation.error && (
            <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/20 p-3 rounded">
              {generateQuestionsMutation.error}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowGenerateDialog(false); generateQuestionsMutation.reset() }} disabled={generateQuestionsMutation.isRunning}>
              {generateQuestionsMutation.isRunning ? 'Generating...' : 'Cancel'}
            </Button>
            {generateQuestionsMutation.done && generateQuestionsMutation.questions.length > 0 && (
              <Button
                disabled={selectedGenerated.size === 0}
                onClick={() => {
                  const selected = generateQuestionsMutation.questions.filter((_: any, i: number) => selectedGenerated.has(i))
                  selected.forEach((q: any) => {
                    createQuestionMutation.mutate({
                      id: q.id || '',
                      domain_id: q.domain_id || '',
                      category: q.category || '',
                      text: q.text,
                      description: q.description || '',
                      question_type: q.question_type || 'mcq',
                      weight: parseFloat(q.weight) || 1.0,
                      maturity_level: parseInt(q.maturity_level) || 1,
                      sort_order: 0,
                      answer_options: q.answer_options || [],
                    })
                  })
                  setShowGenerateDialog(false)
                  generateQuestionsMutation.reset()
                  setSelectedGenerated(new Set())
                }}
              >
                <Check className="w-4 h-4 mr-2" />
                Save {selectedGenerated.size} Question{selectedGenerated.size !== 1 ? 's' : ''}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  )
}

export default QuestionManagement
